import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, CurrencyDollar, Tag, ChartLineUp, ShieldCheck, Truck, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import Logo from "../components/Logo";
import { PartnerLeaderboard } from "../components/PartnerLeaderboard";
import client from "../lib/api";

const money = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const inp = "w-full rounded-md bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5A1F]";

function ApplyForm({ terms }) {
  const [f, setF] = useState({ name: "", email: "", type: "creator", handle: "", audience: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await client.post("/partners/apply", f); setDone(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send — email contact@freightpilotai.ai instead"); }
    finally { setBusy(false); }
  };
  if (done) {
    return (
      <div className="card-surface p-8 text-center" data-testid="partner-apply-done">
        <CheckCircle size={40} weight="fill" className="text-emerald-400 mx-auto mb-3" />
        <h3 className="font-display text-2xl font-semibold">Application received</h3>
        <p className="text-neutral-400 mt-2 text-sm">Check your inbox — you'll get your tracked link and promo code within 2 business days.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="card-surface p-6 sm:p-8 space-y-3" data-testid="partner-apply-form">
      <h3 className="font-display text-2xl font-semibold">Apply in 60 seconds</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={inp} placeholder="Your name" value={f.name} onChange={set("name")} required data-testid="partner-apply-name" />
        <input className={inp} type="email" placeholder="Email" value={f.email} onChange={set("email")} required data-testid="partner-apply-email" />
        <select className={inp} value={f.type} onChange={set("type")} data-testid="partner-apply-type">
          <option value="creator">I'm a creator (TikTok / YouTube / FB group)</option>
          <option value="agent">I work with new carriers (insurance, BOC-3, factoring, dispatch)</option>
        </select>
        <input className={inp} placeholder="@handle, channel or company" value={f.handle} onChange={set("handle")} data-testid="partner-apply-handle" />
      </div>
      <input className={inp} placeholder="Who's your audience? (e.g. 12k new owner-ops on TikTok, 40 new MCs/month at my agency)" value={f.audience} onChange={set("audience")} data-testid="partner-apply-audience" />
      <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="partner-apply-submit">
        <PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending…" : "Send application"}
      </button>
      <p className="text-xs text-neutral-500">Payouts: {money(terms.payouts.creator)} per paying customer for creators · {money(terms.payouts.agent)} for referral partners · paid monthly.</p>
    </form>
  );
}

export default function PartnersPitchPage() {
  const [terms, setTerms] = useState({ payouts: { creator: 5000, agent: 7500 }, promo_pct: 50 });
  useEffect(() => { client.get("/public/partner-terms").then((r) => setTerms(r.data)).catch(() => {}); }, []);
  const cards = [
    { icon: CurrencyDollar, t: `${money(terms.payouts.creator)}–${money(terms.payouts.agent)} per paying customer`, d: "Flat payout every time someone from your link becomes a paying FreightPilot customer. Paid monthly, no cap." },
    { icon: Tag, t: `Your audience gets ${terms.promo_pct}% off`, d: "Your own promo code takes half off their first month — a real reason to use your link instead of Googling it." },
    { icon: ChartLineUp, t: "Live stats + monthly bonuses", d: "Your own dashboard: clicks, signups, paying customers, what you're owed. Hit 3 paying in a month for +$100, 6 for +$300, 10 for +$600 — and a spot on the leaderboard." },
    { icon: ShieldCheck, t: "Something worth sharing", d: "The tools are free without a signup: BrokerCheck catches fake and double brokers in 3 seconds; the New-Authority checklist texts them the day their MC goes ACTIVE." },
  ];
  return (
    <div className="min-h-screen bg-neutral-950 text-white noise-bg" data-testid="partners-pitch-page">
      <header className="max-w-5xl mx-auto px-4 sm:px-8 py-6 flex items-center justify-between">
        <Link to="/"><Logo /></Link>
        <Link to="/broker-check" className="text-sm text-neutral-400 hover:text-white">Try BrokerCheck →</Link>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-8 pb-20">
        <section className="pt-8 sm:pt-16 pb-10 max-w-3xl">
          <div className="overline text-[#FF5A1F] flex items-center gap-1.5 mb-4"><Handshake size={12} weight="fill" /> Partner Program</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Your audience is getting their MC. <span className="text-[#FF5A1F]">Get paid</span> to hand them the tool.
          </h1>
          <p className="mt-6 text-neutral-400 text-base sm:text-lg leading-relaxed">
            FreightPilot AI is built by an owner-operator for new owner-operators: broker scam checks, authority tracking, carrier packets, driver texts.
            If you make trucking content or you're the insurance agent, BOC-3 filer, factoring rep or dispatcher every new carrier talks to — we'll pay you for every one you send.
          </p>
        </section>
        <section className="grid sm:grid-cols-2 gap-4" data-testid="partners-pitch-cards">
          {cards.map((c) => (
            <div key={c.t} className="card-surface p-6">
              <c.icon size={22} weight="fill" className="text-[#FF5A1F] mb-3" />
              <h3 className="font-semibold text-white text-lg">{c.t}</h3>
              <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </section>
        <section className="mt-8"><PartnerLeaderboard /></section>
        <section className="mt-12 grid lg:grid-cols-[1fr_1.1fr] gap-8 items-start">
          <div>
            <div className="overline mb-3">HOW IT WORKS</div>
            <ol className="space-y-4 text-sm text-neutral-300">
              {["Apply below — takes a minute.", "We send you a tracked link (freightpilotai.ai/go/you) and your promo code.", "Put the link in your bio, pin it in a comment, or hand it to clients when their MC is filed.", "Watch your stats page. We pay out monthly for every paying customer."].map((s, i) => (
                <li key={i} className="flex gap-3"><span className="font-display text-[#FF5A1F] font-bold">{i + 1}</span><span>{s}</span></li>
              ))}
            </ol>
            <div className="mt-8 flex items-center gap-2 text-xs text-neutral-500"><Truck size={14} /> Owner-operator founded · US carriers · no exclusivity, no minimums.</div>
          </div>
          <ApplyForm terms={terms} />
        </section>
      </main>
    </div>
  );
}
